import { useEffect, useRef, useState } from 'react';
import { api, errorCode, UnauthenticatedError, type RangeSession } from '../lib/api';
import { drainRefereeSyncBounded, subscribeUnsentForAssignments } from '../lib/sync';
import { userErrorText } from '../lib/userErrorText';
import { SignaturePad, type SignaturePadHandle } from './SignaturePad';

interface Signer {
  id: string;
  displayName: string;
  functionLabel: string;
}

/**
 * Full-screen Badeschluss confirmation overlay for the tablet. Two
 * eligible signers (Standführer / Admin) are selected and sign; on
 * confirm both signatures are uploaded and `sessions.close` is called
 * with the pair. `onClosed` fires once the session is closed.
 */
export function BadeschlussSignatures({
  session,
  onCancel,
  onClosed,
}: {
  session: RangeSession;
  onCancel: () => void;
  onClosed: () => void;
}) {
  const [signers, setSigners] = useState<Signer[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [userA, setUserA] = useState('');
  const [userB, setUserB] = useState('');
  const [filledA, setFilledA] = useState(false);
  const [filledB, setFilledB] = useState(false);
  const padA = useRef<SignaturePadHandle | null>(null);
  const padB = useRef<SignaturePadHandle | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pre-close check (UX review REF-5): started rounds not yet confirmed on
  // the server, and changes of THIS session's rounds still waiting in this
  // tablet's outbox — unsent (pending/inflight) and rejected (failed_terminal)
  // apart. Same statuses as Score's confirm check: rejected uploads are
  // unsaved work too and never reach the server's open-round count.
  const [openRounds, setOpenRounds] = useState<number | null>(null);
  const [unsynced, setUnsynced] = useState({ pending: 0, failed: 0 });
  // This session's rounds (null = roster not loadable → device-wide);
  // undefined while loading.
  const [assignmentIds, setAssignmentIds] = useState<ReadonlySet<string> | null | undefined>(
    undefined,
  );
  const [checkError, setCheckError] = useState(false);
  const [override, setOverride] = useState(false);

  async function loadCheck() {
    setCheckError(false);
    // Try to send what is queued (bounded); the live count below follows it.
    const drained = drainRefereeSyncBounded();
    const [check, roster] = await Promise.allSettled([
      api.closeCheck(session.id),
      api.liveAssignments(session.id),
    ]);
    if (check.status === 'fulfilled') setOpenRounds(check.value.openRounds);
    else setCheckError(true);
    if (roster.status === 'fulfilled') {
      setAssignmentIds(new Set(roster.value.map((r) => r.assignment.id)));
    } else {
      setAssignmentIds((prev) => (prev === undefined ? null : prev));
    }
    await drained;
    // Queued confirmations may have landed meanwhile: count open rounds again.
    if (check.status === 'fulfilled') {
      try {
        setOpenRounds((await api.closeCheck(session.id)).openRounds);
      } catch {
        // Keep the first answer.
      }
    }
  }

  useEffect(() => {
    void loadCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per overlay
  }, []);

  // Re-count after every outbox change (the drain acks entries meanwhile).
  useEffect(() => {
    if (assignmentIds === undefined) return;
    return subscribeUnsentForAssignments(assignmentIds, setUnsynced);
  }, [assignmentIds]);

  useEffect(() => {
    let alive = true;
    api
      .eligibleSigners()
      .then((rows) => alive && setSigners(rows))
      .catch(
        (err) =>
          alive &&
          setLoadError(
            err instanceof UnauthenticatedError
              ? 'Nicht angemeldet.'
              : 'Berechtigte konnten nicht geladen werden.',
          ),
      );
    return () => {
      alive = false;
    };
  }, []);

  const distinct = userA !== '' && userB !== '' && userA !== userB;
  const needsOverride = (openRounds ?? 0) > 0 || unsynced.pending > 0 || unsynced.failed > 0;
  const ready = distinct && filledA && filledB && !busy && (!needsOverride || override);

  async function confirm() {
    setError(null);
    const urlA = padA.current?.toDataUrl();
    const urlB = padB.current?.toDataUrl();
    if (!userA || !userB || userA === userB || !urlA || !urlB) {
      setError('Bitte zwei verschiedene Personen wählen und beide unterschreiben.');
      return;
    }
    setBusy(true);
    try {
      const [blobA, blobB] = await Promise.all([
        api.uploadSignature(urlA),
        api.uploadSignature(urlB),
      ]);
      await api.closeSession(
        session.id,
        [
          { userId: userA, signatureBlobId: blobA },
          { userId: userB, signatureBlobId: blobB },
        ],
        override,
      );
      onClosed();
    } catch (err) {
      // Rounds were confirmed / started meanwhile: refresh the counts so the
      // override checkbox appears (REF-5).
      if (errorCode(err) === 'open_rounds') void loadCheck();
      setError(userErrorText(err));
      setBusy(false);
    }
  }

  function slot(
    label: string,
    value: string,
    onChange: (v: string) => void,
    otherValue: string,
    padRef: React.MutableRefObject<SignaturePadHandle | null>,
    onFilledChange: (filled: boolean) => void,
  ) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-zinc-200">{label}</label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded border border-zinc-600 bg-zinc-800 px-2 py-2 text-base text-zinc-50"
        >
          <option value="">— Person wählen —</option>
          {(signers ?? []).map((s) => (
            <option key={s.id} value={s.id} disabled={s.id === otherValue}>
              {s.displayName} ({s.functionLabel})
            </option>
          ))}
        </select>
        <SignaturePad handleRef={padRef} onChange={(empty) => onFilledChange(!empty)} />
        <button
          type="button"
          onClick={() => {
            padRef.current?.clear();
            onFilledChange(false);
          }}
          className="text-xs text-zinc-400 underline"
        >
          Unterschrift löschen
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-zinc-950/95 p-4">
      <div className="mx-auto max-w-3xl">
        <h2 className="text-xl font-bold">🛁 Badeschluss — zwei Unterschriften</h2>
        <p className="mt-1 text-sm text-zinc-400">
          Zwei Berechtigte (Standführer oder Admin) bestätigen den Abschluss von „{session.name}“.
          Die Unterschriften erscheinen auf den Ergebnislisten.
        </p>

        {needsOverride && (
          <div
            role="alert"
            className="mt-4 space-y-2 rounded-lg border border-amber-600 bg-amber-950/40 p-3 text-sm text-amber-100"
          >
            {(openRounds ?? 0) > 0 && (
              <p>
                ⚠ {openRounds} begonnene {openRounds === 1 ? 'Runde ist' : 'Runden sind'} noch nicht
                bestätigt.
              </p>
            )}
            {unsynced.pending > 0 && (
              <p>
                ⚠ {unsynced.pending} {unsynced.pending === 1 ? 'Änderung ist' : 'Änderungen sind'}{' '}
                auf diesem Tablet noch nicht übertragen. Bitte Verbindung prüfen und kurz warten.
              </p>
            )}
            {unsynced.failed > 0 && (
              <p>
                ⚠ {unsynced.failed} {unsynced.failed === 1 ? 'Änderung wurde' : 'Änderungen wurden'}{' '}
                vom Server abgelehnt. Bitte in der Wertung der betroffenen Person erneut senden oder
                verwerfen.
              </p>
            )}
            <label className="flex min-h-12 items-center gap-3">
              <input
                type="checkbox"
                checked={override}
                onChange={(e) => setOverride(e.target.checked)}
                className="h-6 w-6"
              />
              Trotzdem Badeschluss — offene Runden bleiben unbestätigt
            </label>
            <button
              type="button"
              onClick={() => void loadCheck()}
              className="min-h-12 text-xs text-amber-200 underline"
            >
              Erneut prüfen
            </button>
          </div>
        )}
        {checkError && (
          <p className="mt-4 text-sm text-amber-300">
            Offene Runden konnten nicht geprüft werden — der Server prüft beim Bestätigen erneut.
          </p>
        )}

        {loadError ? (
          <p className="mt-6 text-sm text-red-400">{loadError}</p>
        ) : signers === null ? (
          <p className="mt-6 text-sm text-zinc-400">Lade berechtigte Personen…</p>
        ) : signers.length < 2 ? (
          <p className="mt-6 text-sm text-red-400">
            Es sind weniger als zwei berechtigte Personen (Standführer/Admin) im System — ein
            Badeschluss mit zwei Unterschriften ist nicht möglich.
          </p>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {slot('1. Unterschrift', userA, setUserA, userB, padA, setFilledA)}
            {slot('2. Unterschrift', userB, setUserB, userA, padB, setFilledB)}
          </div>
        )}

        {error && <p className="mt-4 text-sm text-red-400">{error}</p>}

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 rounded-lg border border-zinc-600 py-3 text-base font-semibold disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={!ready}
            className="flex-1 rounded-lg bg-red-600 py-3 text-base font-bold disabled:opacity-40"
          >
            {busy ? 'Schließe…' : 'Badeschluss bestätigen'}
          </button>
        </div>
      </div>
    </div>
  );
}
